import React, { useEffect, useRef, useState } from 'react';

/**
 * Confirm step for an admin removing a competition check-in (#78). For a paid
 * registration it says the refund is separate, because removing the row does
 * not move any money.
 */
interface RemoveCheckInDialogProps {
  registration: {
    id: string;
    participantName?: string | null;
    feeAmount?: number | null;
    squarePaymentId?: string | null;
    registrationStatus?: string | null;
  } | null;
  paid: boolean;
  removing: boolean;
  error?: string | null;
  onCancel: () => void;
  /** #163: a reason is required (and passed) when the check-in was already approved. */
  onConfirm: (reason?: string) => void;
}

const RemoveCheckInDialog: React.FC<RemoveCheckInDialogProps> = ({ registration, paid, removing, error, onCancel, onConfirm }) => {
  const cancelRef = useRef<HTMLButtonElement>(null);
  const [reason, setReason] = useState('');
  useEffect(() => {
    if (registration) cancelRef.current?.focus();
    setReason('');
  }, [registration]);
  if (!registration) return null;
  const name = registration.participantName || 'this person';
  const fee = registration.feeAmount ? ` $${registration.feeAmount}` : '';
  const online = !!registration.squarePaymentId;
  const approved = registration.registrationStatus === 'FINALIZED';
  const needsReason = approved && !reason.trim();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="remove-checkin-title"
        aria-describedby="remove-checkin-desc"
        onKeyDown={(e) => {
          if (e.key === 'Escape' && !removing) onCancel();
        }}
        className="w-full max-w-md rounded-xl bg-white p-5 shadow-2xl"
      >
        <h2 id="remove-checkin-title" className="text-lg font-bold text-gray-900">Remove check-in?</h2>
        <div id="remove-checkin-desc" className="mt-2 space-y-2 text-sm text-gray-700">
          <p>
            This removes <strong>{name}</strong> from today&apos;s check-ins. Their catches are not deleted.
          </p>
          {approved && (
            <p className="font-medium text-gray-900">{name} is already checked in (approved).</p>
          )}
          {paid && (
            <p className="rounded-md border border-amber-300 bg-amber-50 p-2 font-medium text-amber-900">
              {online
                ? `${name} paid${fee} online. Refund it separately in Square.`
                : `${name} paid${fee} at the desk. Give the money back at the desk.`}
            </p>
          )}
          {approved && (
            <div>
              <label htmlFor="remove-checkin-reason" className="block text-sm font-medium text-gray-700">
                Reason for removing (required)
              </label>
              <input
                id="remove-checkin-reason"
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="mt-1 w-full min-h-[44px] rounded-md border border-gray-300 px-3 py-2 text-sm"
                placeholder="For example: checked in by mistake"
              />
            </div>
          )}
        </div>
        {error && (
          <p role="alert" className="mt-3 text-sm font-medium text-red-700">{error}</p>
        )}
        <div className="mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button
            ref={cancelRef}
            type="button"
            onClick={onCancel}
            disabled={removing}
            className="min-h-[44px] rounded-lg border border-gray-300 px-4 py-2 font-semibold text-gray-800 hover:bg-gray-50 disabled:opacity-50"
          >
            Keep check-in
          </button>
          <button
            type="button"
            onClick={() => onConfirm(approved ? reason.trim() : undefined)}
            disabled={removing || needsReason}
            className="min-h-[44px] rounded-lg bg-red-700 px-4 py-2 font-semibold text-white hover:bg-red-800 disabled:opacity-50"
          >
            {removing ? 'Removing…' : 'Remove check-in'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RemoveCheckInDialog;
